import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Trash2, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  computeAvailableFieldsAt,
  type BranchNodeData,
  type EndNodeData,
  type PreconditionNodeData,
  type PricingNodeData,
  type StaffNodeData,
  type UserFormNodeData,
} from "@/lib/productGraph";
import type { AutomationNodeData, CreateRequestNodeData, DecisionNodeData, DocumentCollectionNodeData, InternalRequestNodeData, InviteActorsNodeData } from "@gestionalo/enterprise-graph";
import type { ProductGraph, ProductNode } from "@/services/platformEnterprisesService";

import { BranchEditor } from "./editors/branch-editor";
import { EndEditor } from "./editors/end-editor";
import { InternalRequestEditor } from "./editors/internal-request-editor";
import { DecisionEditor } from "./editors/decision-editor";
import { InviteActorsEditor } from "./editors/invite-actors-editor";
import { PreconditionEditor } from "./editors/precondition-editor";
import { PricingEditor } from "./editors/pricing-editor";
import { StaffEditor } from "./editors/staff-editor";
import { AutomationEditor } from "./editors/automation-editor";
import { DocumentCollectionEditor } from "./editors/document-collection-editor";
import { UserFormEditor } from "./editors/user-form-editor";

interface Props {
  graph: ProductGraph;
  node: ProductNode | null;
  onUpdateData: (id: string, patch: Record<string, unknown>) => void;
  onRemoveNode: (id: string) => void;
  onClose?: () => void;
  enterpriseId: string;
  productId: string;
}

export function ProductBuilderDetailPanel({
  graph,
  node,
  onUpdateData,
  onRemoveNode,
  onClose,
  enterpriseId,
  productId,
}: Props): JSX.Element {
  const { t } = useTranslation();

  const availableFields = useMemo(() => {
    if (!node) return [];
    return computeAvailableFieldsAt(graph, node.id);
  }, [graph, node]);

  if (!node) {
    return (
      <div className="flex h-full flex-col">
        {onClose && (
          <div className="flex shrink-0 items-center justify-end border-b bg-muted/30 px-2 py-2">
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={onClose}
              aria-label={t("enterpriseProductBuilder.closeDrawer")}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
        <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-muted-foreground">
          {t("enterpriseProductBuilder.selectNodeHint")}
        </div>
      </div>
    );
  }

  const handlePatch = (patch: Record<string, unknown>): void => {
    onUpdateData(node.id, patch);
  };

  return (
    <div className="flex h-full flex-col">
      <div className="border-b bg-muted/30 px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              {t(`enterpriseProductBuilder.nodeType.${node.type}`)}
            </div>
            <code className="block truncate text-[10px] text-muted-foreground">
              {node.id}
            </code>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {node.type !== "start" && (
              <Button
                size="sm"
                variant="ghost-destructive"
                className="h-7"
                onClick={() => onRemoveNode(node.id)}
              >
                <Trash2 className="mr-1 h-3.5 w-3.5" />
                {t("enterpriseProductBuilder.removeNode")}
              </Button>
            )}
            {onClose && (
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                onClick={onClose}
                aria-label={t("enterpriseProductBuilder.closeDrawer")}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
      <Separator />
      <div className="flex-1 overflow-y-auto p-4">
        {node.type === "start" && (
          <p className="text-sm text-muted-foreground">
            {t("enterpriseProductBuilder.startNodeHint")}
          </p>
        )}
        {node.type === "precondition" && (
          <PreconditionEditor
            data={node.data as unknown as PreconditionNodeData}
            onChange={handlePatch}
          />
        )}
        {node.type === "userForm" && (
          <UserFormEditor
            data={node.data as unknown as UserFormNodeData}
            onChange={handlePatch}
          />
        )}
        {node.type === "staffApproval" && (
          <StaffEditor
            data={node.data as unknown as StaffNodeData}
            onChange={handlePatch}
          />
        )}
        {node.type === "staffAction" && (
          <StaffEditor
            data={node.data as unknown as StaffNodeData}
            onChange={handlePatch}
            showFields
          />
        )}
        {node.type === "branch" && (
          <BranchEditor
            data={node.data as unknown as BranchNodeData}
            onChange={handlePatch}
            availableFields={availableFields}
          />
        )}
        {node.type === "pricing" && (
          <PricingEditor
            data={node.data as unknown as PricingNodeData}
            onChange={handlePatch}
            availableFields={availableFields}
          />
        )}
        {node.type === "internalRequest" && (
          <InternalRequestEditor
            data={node.data as unknown as InternalRequestNodeData}
            onChange={handlePatch}
            enterpriseId={enterpriseId}
            productId={productId}
            availableFields={availableFields}
          />
        )}
        {node.type === "inviteActors" && (
          <InviteActorsEditor
            data={node.data as unknown as InviteActorsNodeData}
            onChange={handlePatch}
          />
        )}
        {node.type === "decision" && (
          <DecisionEditor
            data={node.data as unknown as DecisionNodeData}
            onChange={handlePatch}
            graph={graph}
            nodeId={node.id}
          />
        )}
        {node.type === "createRequest" && (
          <EndEditor
            data={node.data as unknown as CreateRequestNodeData}
            onChange={handlePatch}
          />
        )}
        {node.type === "automation" && (
          <AutomationEditor
            data={node.data as unknown as AutomationNodeData}
            onChange={handlePatch}
            availableFields={availableFields}
            graph={graph}
            nodeId={node.id}
          />
        )}
        {node.type === "documentCollection" && (
          <DocumentCollectionEditor
            data={node.data as unknown as DocumentCollectionNodeData}
            onChange={handlePatch}
            availableFields={availableFields}
            graph={graph}
            nodeId={node.id}
          />
        )}
        {node.type === "end" && (
          <EndEditor
            data={node.data as unknown as EndNodeData}
            onChange={handlePatch}
          />
        )}
      </div>
    </div>
  );
}
